import { Module } from 'vuex'
import { Store, User } from '@/types'
import {
  SleepItem,
  PainItem,
  ActivitiesByLocalDate,
  GroupedActivityItem
} from '@/types/user/API/ReqRes/userActivity'
import apiFactory from '@api/activities'
import get from 'lodash/get'
import moment from 'moment-timezone'

const api = apiFactory()

const storeModule: Module<User.UserActivitiesState, Store.RootState> = {
  namespaced: true,
  state: {
    all: []
  },
  mutations: {
    SET_ALL: (state, list: User.API.ReqRes.UserActivity.Items) => {
      state.all = list
    },
    ADD_TO_ALL: (state, data: User.API.ReqRes.UserActivity.Items | User.API.ReqRes.UserActivity.Item) => {
      if (Array.isArray(data)) {
        state.all.push(...data)
      }
      else {
        state.all.push(data)
      }
    }
  },
  actions: {
    async GetList ({ commit }) {
      commit('SET_ALL', await api.getList())
    },
    async CreateOne ({ commit, dispatch }, data: User.API.ReqRes.UserActivity.Item) {
      commit(
        'ADD_TO_ALL',
        await api.createOne({
          activityTimestamp: Date.now(),
          zoneId: moment.tz.guess(),
          ...data
        })
      )
      dispatch('user/GetReportURL', null, { root: true })
    },
    async CreateManyDry (_, data: User.API.ReqRes.UserActivity.Items) {
      console.log(data)
    },
    async CreateMany ({ commit, dispatch }, data: User.API.ReqRes.UserActivity.Items) {
      commit(
        'ADD_TO_ALL',
        await api.createMany(
          data.map(
            activity => ({
              activityTimestamp: Date.now(),
              zoneId: moment.tz.guess(),
              ...activity
            })
          )
        )
      )
      dispatch('user/GetReportURL', null, { root: true })
    },
    async SetGoal ({ dispatch, getters, rootGetters }, content: string) {
      await dispatch('CreateOne', {
        content,
        activityTimestamp: Date.now(),
        type: 'Goal',
        zoneId: rootGetters['user/me'].patientTZ || moment.tz.guess()
      })
      return getters.lastOfType('Goal')
    },
    async SetAssessment ({ dispatch, getters, rootGetters }, content: string) {
      await dispatch('CreateOne', {
        content,
        activityTimestamp: Date.now(),
        type: 'Assessment',
        zoneId: rootGetters['user/me'].patientTZ || moment.tz.guess()
      })
      return getters.lastOfType('Assessment')
    }
  },
  getters: {
    all: state => state.all,
    lastOfType: state => (lookupType: User.API.ReqRes.UserActivity.ItemTypes) => {
      const filtered = state.all
        .filter(
          ({ type }) => type === lookupType
        )
        .sort(
          (a, b) => b.activityTimestamp - a.activityTimestamp
        )
      return filtered.length ? filtered[0] : null
    },
    goal: (_, getters) => {
      return get(getters.lastOfType('Goal'), 'content', '')
    },
    assessment: (_, getters) => {
      return get(getters.lastOfType('Assessment'), 'content', '')
    },
    groupedByLocalDate: state => {
      return state.all.reduce(
        (acc, item) => {
          const {
            activityLocalDate: localDate,
            type,
            activityTimestamp: timeStamp
          } = item
          if (type === 'Sleep' || type === 'Pain') {
            if (!acc[localDate]) {
              acc[localDate] = {
                sleepLevel: null,
                painLevel: null,
                activityLevel: null,
                sleepNote: null,
                painNote: null,
                localDate,
                lastRecord: {
                  Sleep: 0,
                  Pain: 0
                }
              }
            }
            const storedItem = get(acc, localDate) as GroupedActivityItem
            if (timeStamp > storedItem.lastRecord[type]) {
              storedItem.lastRecord[type] = timeStamp
              switch (type) {
                case 'Sleep':
                  storedItem.sleepLevel = (item as SleepItem).sleepLevel
                  storedItem.sleepNote = (item as SleepItem).note || null
                  break
                case 'Pain':
                  storedItem.requestCallback = (item as PainItem).requestCallback
                  storedItem.painLevel = (item as PainItem).painLevel
                  storedItem.activityLevel = (item as PainItem).activityLevel
                  storedItem.painNote = (item as PainItem).note || null
                  break
              }
            }
          }
          return acc
        }, {} as ActivitiesByLocalDate
      )
    }
  }
}

export default storeModule
